/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, ModuleRegistry, AllCommunityModule, themeAlpine } from "ag-grid-community";
import { Tooltip, IconButton } from "@mui/material";
import { AiOutlineFileSearch } from "react-icons/ai";
import { MdOutlineContentCopy } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import AgGridPagination from "../../shared/AgGridPagination";

dayjs.extend(utc);
dayjs.extend(timezone);

ModuleRegistry.registerModules([AllCommunityModule]);

type RowData = {
  id: string;
  suba_name: string;
};

const ReceiverAccountList: React.FC<{
  isLoading: boolean;
  page: number;
  pageSize: number;
  rowCount: number;
  tableData: RowData[];
  setPaginationModel: any;
  currentLastKey: string;
  entityType?: string;
}> = ({
  isLoading,
  page,
  pageSize,
  rowCount,
  tableData,
  setPaginationModel,
  currentLastKey,
  entityType,
}) => {
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const handleClickDetail = (id: any) => {
    if (entityType === "AGNT#") {
      navigate(`/${userRole}/agents/${id}`);
    } else {
      navigate(`/${userRole}/merchants/subaccounts/${id}`);
    }
  };

  const CustomLoadingOverlay = () => (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
      <CircularProgress size={40} />
    </div>
  );

  const CopyIdRenderer = (params: ICellRendererParams) => (
    <Tooltip title="Copy ID" arrow placement="top">
      <div>
        <IconButton
          id={"button-copy-id-" + params.data?.id}
          onClick={() => handleClickCopy(params.data.id)}
          size="small"
        >
          <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const ActionsRenderer = (params: ICellRendererParams) => (
    <Tooltip title="View More Details" arrow placement="right">
      <div className="mr-5">
        <IconButton
          id={"button-detail-" + params.data?.id}
          onClick={() => handleClickDetail(params.data.id)}
          size="small"
        >
          <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
        </IconButton>
      </div>
    </Tooltip>
  );

  const columns: ColDef[] = [
    {
      field: "id",
      headerName: "",
      width: 50,
      sortable: false,
      resizable: false,
      cellRenderer: CopyIdRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
    },
    {
      field: "suba_name",
      headerName: entityType === "AGNT#" ? "Agent" : "Subaccount",
      sortable: false,
      minWidth: 150,
      flex: 1,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      width: 100,
      resizable: false,
      cellRenderer: ActionsRenderer,
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "flex-end" },
    },
  ];

  const defaultColDef = useMemo<ColDef>(
    () => ({
      resizable: true,
      suppressMenu: true,
    }),
    []
  );

  const hidePagination = currentLastKey === "" && page === 0;

  return (
    <div className="px-4 py-8">
      <Box>
        <div style={{ width: "100%" }}>
          <AgGridReact
            theme={themeAlpine}
            rowData={isLoading ? [] : tableData}
            columnDefs={columns}
            defaultColDef={defaultColDef}
            getRowId={(params) => params.data.id}
            rowHeight={48}
            headerHeight={44}
            domLayout="autoHeight"
          suppressDragLeaveHidesColumns={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
            suppressPaginationPanel={true}
            loading={isLoading}
            loadingOverlayComponent={CustomLoadingOverlay}
            overlayNoRowsTemplate="No data available"
          />
        </div>

        <AgGridPagination
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          onPaginationChange={setPaginationModel}
          hidden={hidePagination}
        />
      </Box>
    </div>
  );
};

export default ReceiverAccountList;
